import { useState } from 'react';
import { T, useGT } from 'gt-react';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { Input } from '@/components/ui/input';

interface DeletePricingEntityDialogProps {
  open: boolean;
  title: string;
  warning: React.ReactNode;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function DeletePricingEntityDialog({
  open,
  title,
  warning,
  loading,
  onClose,
  onConfirm,
}: DeletePricingEntityDialogProps) {
  const gt = useGT();
  const [confirmText, setConfirmText] = useState('');
  const [prevOpen, setPrevOpen] = useState(open);

  // Reset during render (not an effect) whenever the controlled `open` prop
  // transitions to closed, including closes triggered by the parent after a
  // successful delete rather than through handleClose below.
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (!open) {
      setConfirmText('');
    }
  }

  function handleConfirm() {
    onConfirm();
  }

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      title={title}
      dangerous
      loading={loading}
      disabled={confirmText !== 'delete'}
      confirmText={gt('Delete', {
        context: 'confirm button, delete pricing entity',
      })}
      closeOnConfirm={false}
    >
      <div className="space-y-4">
        <p className="text-sm text-destructive font-medium">{warning}</p>

        <div className="space-y-2">
          <label className="text-sm text-muted-foreground">
            <T context="pricing delete confirmation instruction">
              Type <span className="font-mono font-semibold">delete</span> to
              confirm
            </T>
          </label>
          <Input
            value={confirmText}
            onChange={(event) => setConfirmText(event.target.value)}
            placeholder={gt('delete', {
              context: 'input placeholder, user must type delete to confirm',
            })}
            className="max-w-[200px]"
          />
        </div>
      </div>
    </ConfirmDialog>
  );
}
